import type { MeDto } from '@dijitalapp/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { AuthLayout, useSubmit } from './AuthPages';

/**
 * Email links carry the token in the URL fragment. It is read once and removed from the address
 * bar so it does not linger in history or get shared by copy-paste.
 */
function takeTokenFromHash(): string | null {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  if (token) history.replaceState(null, '', window.location.pathname);
  return token;
}

export function ForgotPasswordPage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, busy, run } = useSubmit();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/auth/password/forgot', { email });
      setSent(true);
    });
  };

  return (
    <AuthLayout title={t('auth.forgot.title')}>
      {sent ? (
        <div className="alert alert-info" role="status">
          {t('auth.forgot.sent')}
        </div>
      ) : (
        <form className="stack" onSubmit={onSubmit} noValidate>
          <p className="muted">{t('auth.forgot.body')}</p>
          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}
          <div className="field">
            <label htmlFor="email">{t('auth.email')}</label>
            <input id="email" className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-block" disabled={busy}>
            {t('auth.forgot.submit')}
          </button>
        </form>
      )}
      <p className="muted">
        <Link to="/login">{t('auth.login.title')}</Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { i18n, setMe } = useApp();
  const { t } = i18n;
  const { error, busy, run } = useSubmit();
  const [token] = useState(takeTokenFromHash);
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/auth/password/reset', { token, password });
      // Every session, including this browser's, was revoked by the reset.
      setMe(null);
      setDone(true);
    });
  };

  return (
    <AuthLayout title={t('auth.reset.title')}>
      {!token ? (
        <div className="alert alert-error" role="alert">
          {t('error.EMAIL_TOKEN_INVALID')}
        </div>
      ) : done ? (
        <div className="alert alert-info" role="status">
          {t('auth.reset.done')}
        </div>
      ) : (
        <form className="stack" onSubmit={onSubmit} noValidate>
          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}
          <div className="field">
            <label htmlFor="new-password">{t('auth.reset.newPassword')}</label>
            <input
              id="new-password"
              className="input"
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              aria-describedby="new-password-hint"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="hint" id="new-password-hint">
              {t('auth.passwordHint')}
            </span>
          </div>
          <button className="btn btn-primary btn-block" disabled={busy}>
            {t('auth.reset.submit')}
          </button>
        </form>
      )}
      <p className="muted">
        <Link to="/login">{t('auth.login.title')}</Link>
      </p>
    </AuthLayout>
  );
}

// Tokens are single use: effects that re-run (StrictMode, remounts) must share one request.
const verifications = new Map<string, Promise<MeDto>>();
function verifyOnce(token: string): Promise<MeDto> {
  let pending = verifications.get(token);
  if (!pending) {
    pending = api<MeDto>('POST', '/auth/email/verify', { token });
    verifications.set(token, pending);
  }
  return pending;
}

export function VerifyEmailPage() {
  const { i18n, me, setMe } = useApp();
  const { t } = i18n;
  const [token] = useState(takeTokenFromHash);
  const [state, setState] = useState<{ status: 'working' | 'ok' | 'error'; message?: string }>(() =>
    token ? { status: 'working' } : { status: 'error', message: t('error.EMAIL_TOKEN_INVALID') },
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    verifyOnce(token).then(
      (verified) => {
        if (cancelled) return;
        setState({ status: 'ok' });
        // Update the signed-in view only if it is the same account.
        setMe((current) => (current?.id === verified.id ? verified : current));
      },
      (err: unknown) => !cancelled && setState({ status: 'error', message: t(errorMessageKey(err)) }),
    );
    return () => {
      cancelled = true;
    };
  }, [token, setMe, t]);

  return (
    <AuthLayout title={t('auth.verify.title')}>
      {state.status === 'working' && <p role="status">{t('auth.verify.working')}</p>}
      {state.status === 'ok' && (
        <div className="alert alert-info" role="status">
          {t('auth.verify.success')}
        </div>
      )}
      {state.status === 'error' && (
        <div className="alert alert-error" role="alert">
          {state.message}
        </div>
      )}
      <Link className="btn btn-primary btn-block" to={me ? '/' : '/login'}>
        {me ? t('auth.verify.continue') : t('auth.login.title')}
      </Link>
    </AuthLayout>
  );
}
